import React from 'react';
import styled, { createGlobalStyle } from 'styled-components';
import Home from '../Home';

// --- Global Styles (Rest of the styles remain the same for brevity) ---
const GlobalStyle = createGlobalStyle`
  body {
    margin: 0;
    padding: 0;
    font-family: Arial, sans-serif;
    color: #333;
    line-height: 1.6;
    background-color: #fff;
  }
  * {
    box-sizing: border-box;
  }
`;

// --- Styled Components for Layout and Structure ---
// (All previously defined styled components like PageContainer, HeaderWrapper,
// SymphonySection, ChooseSection, ServicesSection, FaqSection, etc., are assumed
// to be here. Only FaqItem is slightly adjusted to handle the new structure)

const PageContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
`;

const HeaderWrapper = styled.header`
  background: #000;
  color: #fff;
  text-align: center;
  padding: 100px 20px;
  position: relative;
  overflow: hidden;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url(background-texture.jpg) no-repeat center center;
    background-size: cover;
    opacity: 0.3;
    z-index: 1;
  }
`;

const HeaderContent = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 350px;
`;

const HeaderTitle = styled.h1`
  font-size: 3em;
  font-weight: 900;
  text-transform: uppercase;
  margin: 0 0 20px 0;
  letter-spacing: 5px;
  span {
    color: gold;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
  }
`;

const HeaderSubtitle = styled.p`
  font-size: 1.5em;
  font-weight: 300;
  margin: 0 0 40px 0;
  color: #ccc;
`;

const EditIcons = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
`;

const Icon = styled.div`
  background: #fff;
  border-radius: 50%;
  padding: 15px;
  color: #333;
  font-size: 1.5em;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
  &::before {
    content: '📝';
  }
  &:nth-child(2)::before {
    content: '✏️';
  }
`;

const NavBar = styled.nav`
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 10;
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    text-align: right;
  }
  li {
    font-size: 0.8em;
    color: #fff;
    margin-bottom: 5px;
  }
`;

const SymphonySection = styled.section`
  padding: 60px 20px;
  background-color: #fff;
  text-align: center;
`;

const SymphonyTitle = styled.h2`
  font-size: 2.2em;
  color: #333;
  margin-bottom: 50px;
  font-weight: 400;
  span {
    font-weight: 700;
    color: #d8b242;
  }
`;

const SymphonySteps = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 800px;
  margin: 0 auto;
  padding: 0 20px;
`;

const StepItem = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin-bottom: 30px;
  text-align: left;
`;

const StepNumber = styled.div`
  width: 40px;
  height: 40px;
  min-width: 40px;
  border: 2px solid #d8b242;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2em;
  font-weight: 700;
  color: #d8b242;
  margin-right: 20px;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    top: 40px;
    bottom: -30px;
    left: 50%;
    width: 2px;
    background-color: #d8b242;
    z-index: -1;
    ${props => props.$last ? 'display: none;' : ''}
  }
`;

const StepContent = styled.div`
  h3 {
    margin: 0 0 5px 0;
    font-size: 1.2em;
    color: #333;
  }
  p {
    margin: 0;
    font-size: 0.9em;
    color: #666;
  }
`;

const ChooseSection = styled.section`
  padding: 60px 20px;
  background-color: #f8f8f8;
  text-align: center;
`;

const ChooseTitle = styled.h2`
  font-size: 1.8em;
  color: #333;
  margin-bottom: 40px;
  font-weight: 400;
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 30px;
  max-width: 1100px;
  margin: 0 auto;
  text-align: left;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  padding: 15px;
  border-radius: 8px;

  h4 {
    margin: 0 0 5px 0;
    font-size: 1.1em;
    color: #333;
  }
  p {
    margin: 0;
    font-size: 0.85em;
    color: #666;
  }
`;

const FeatureIcon = styled.div`
  width: 50px;
  height: 50px;
  background-color: ${props => props.$bgColor || '#eee'};
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5em;
  color: #fff;
  &::before {
    content: '${props => props.$iconText || '✨'}';
  }
`;

const ServicesSection = styled.section`
  background-color: #333;
  color: #fff;
  padding: 60px 20px;
  text-align: center;
`;

const ServicesTitle = styled.h2`
  font-size: 2em;
  margin-bottom: 50px;
  font-weight: 400;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  max-width: 1000px;
  margin: 0 auto;
`;

const ServiceCard = styled.div`
  background-color: rgba(255, 255, 255, 0.1);
  padding: 20px;
  border-radius: 8px;
  text-align: left;
  min-height: 200px;
  
  h3 {
    font-size: 1.2em;
    margin-top: 0;
    color: #d8b242;
  }
  p {
    font-size: 0.9em;
    color: #ccc;
  }
`;

const FaqSection = styled.section`
  padding: 40px 20px;
  background-color: #fff;
  text-align: center;
`;

const FaqTitle = styled.h2`
  font-size: 2em;
  color: #333;
  margin-bottom: 40px;
  font-weight: 600;
`;

const FaqContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  max-width: 900px;
  margin: 0 auto;
  text-align: left;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FaqItem = styled.div`
  position: relative;
  padding-left: 15px;

  h4 {
    margin: 0 0 5px 0;
    font-size: 1em;
    color: #333;
    font-weight: 600;
    position: relative;
    padding-left: 15px; /* Adjust for the bullet */

    &::before {
      content: '▶';
      position: absolute;
      left: 0;
      color: #d8b242;
      font-size: 0.7em;
      top: 3px;
    }
  }

  p {
    margin: 0 0 15px 15px;
    font-size: 0.9em;
    color: #666;
    border-left: 2px solid #eee;
    padding-left: 10px;
  }
`;

// --- Main React Component ---
const Emailmarketing = () => {
    // Data structures for easy mapping and content management
    const symphonySteps = [
        { num: 1, title: "Define Your Strategy", description: "This phase involves setting clear objectives, identifying your target audience, and outlining the campaign structure." },
        { num: 2, title: "Build Your Email List", description: "Creating a robust list of subscribers through ethical and effective lead generation techniques." },
        { num: 3, title: "Craft Compelling Content", description: "Developing engaging and relevant email copy that resonates with your audience and drives action." },
        { num: 4, title: "Design Eye-Catching Layouts", description: "Ensuring emails are visually appealing, mobile-responsive, and adhere to branding guidelines." },
        { num: 5, title: "Test and Optimise", description: "Rigorous A/B testing on subject lines, content, and send times to maximise open and click-through rates." },
        { num: 6, title: "Measure and Analyse", description: "Tracking key performance indicators (KPIs) and using data to refine future marketing efforts." },
    ];

    const featureIcons = [
        { title: "Enhanced Deliverability", description: "Our technology ensures your emails land in the inbox, not the spam folder, by maintaining sender reputation.", iconText: '✉️', bgColor: '#4CAF50' },
        { title: "Behavioral Triggers", description: "Automated emails based on user actions (e.g., abandoned cart, sign-up) for timely engagement.", iconText: '🤖', bgColor: '#2196F3' },
        { title: "Advanced Segmentation", description: "Dividing your list into smaller groups based on demographics, purchase history, and behavior for precise targeting.", iconText: '📊', bgColor: '#F44336' },
        { title: "Dynamic Content Integration", description: "Personalizing email content, images, and offers in real-time based on subscriber data.", iconText: '🔗', bgColor: '#FF9800' },
        { title: "A/B Testing Innovation", description: "Beyond basic testing, we deploy multivariate testing to find the optimal combination for conversion.", iconText: '🧪', bgColor: '#9C27B0' },
        { title: "Full-Funnel Reporting", description: "Comprehensive reports on how email campaigns contribute to overall sales and customer lifetime value.", iconText: '📈', bgColor: '#00BCD4' },
    ];
    
    const services = [
        { title: "Strategic Campaign Planning", content: "Our experts work with you to understand your business goals, target audience, and current marketing landscape to develop a bespoke email strategy." },
        { title: "Engaging Content Creation", content: "We craft compelling subject lines, captivating body copy, and clear calls-to-action that maximize engagement and drive desired outcomes." },
        { title: "Segmentation and Personalisation", content: "Leverage advanced data to segment your audience and deliver hyper-personalized emails, increasing relevance and conversion rates." },
        { title: "Responsive Design Mastery", content: "Our design team ensures your emails look flawless and function perfectly on every device, from desktop to mobile." },
        { title: "Automation Workflow Development", content: "We set up sophisticated email journeys and automation sequences, saving you time and ensuring timely, relevant communication." },
        { title: "In-Depth Analytics and Optimization", content: "We provide detailed performance reports and continuous optimization recommendations to ensure your email program is always improving." },
    ];

    // --- POPULATED FAQ DATA STRUCTURE ---
    const faqItems = [
        { question: "What is email marketing?", 
          answer: "Email marketing is a highly effective digital strategy of sending direct electronic messages to prospects and customers. It’s used to build relationships, promote products/services, and increase brand loyalty, offering a significant ROI when done correctly." },
        
        { question: "How do you ensure GDPR compliance?", 
          answer: "We ensure full compliance by adhering to strict consent protocols, utilizing double opt-in processes, and always including clear, functional unsubscribe links in every email, as required by GDPR and CAN-SPAM laws." },
        
        { question: "Can you integrate with my existing CRM?", 
          answer: "Yes, SRK Software has expertise in integrating with all major CRM platforms (e.g., Salesforce, HubSpot, Zoho, etc.) and Email Service Providers (ESPs) to ensure seamless data flow and personalized campaign execution." },
        
        { question: "What are your typical results?", 
          answer: "While results vary by industry and current list health, our campaigns typically see open rates above the 20% industry standard and click-through rates between 2-5% or higher, with a strong focus on maximizing your revenue per email." },
        
        { question: "How is pricing structured?", 
          answer: "We offer flexible pricing, typically a monthly retainer based on the scope of work (e.g., number of emails, complexity of automation, list size). This provides comprehensive access to strategy, design, copywriting, and deployment services." },
        
        { question: "Do you handle list cleaning?", 
          answer: "Absolutely. List hygiene is critical for deliverability. We regularly audit and clean your list to remove inactive, invalid, or bounced email addresses, which protects your sender reputation and maximizes campaign effectiveness." },
        
        { question: "What is the average setup time?", 
          answer: "The onboarding and initial strategy phase usually takes 2-4 weeks. This includes audience research, platform integration, and setting up the foundational automated flows (like the Welcome Series). Live campaign deployment follows immediately after." },
        
        { question: "How often will we meet?", 
          answer: "We typically schedule a bi-weekly strategy and reporting call to review performance metrics, discuss upcoming content, and align on goals. Your dedicated account manager is also available via email and a shared communication channel for daily support." },
    ];
    // ------------------------------------------

    return (
        <Home>
            <GlobalStyle />
            <PageContainer>
                {/* --- Header Section --- */}
                <HeaderWrapper>
                    <HeaderContent>
                        <HeaderSubtitle>SRK Software</HeaderSubtitle>
                        <HeaderTitle>Empower Business Growth Through <span>Emails</span></HeaderTitle>
                        <EditIcons>
                            <Icon />
                            <Icon />
                            <Icon />
                        </EditIcons>
                    </HeaderContent>
                </HeaderWrapper>

                {/* --- 6-Step Symphony Section --- */}
                <SymphonySection>
                    <SymphonyTitle>Crafting success: Email marketing's <span>6-step symphony</span></SymphonyTitle>
                    <SymphonySteps>
                        {symphonySteps.map((step, index) => (
                            <StepItem key={step.num}>
                                <StepNumber $last={index === symphonySteps.length - 1}>{step.num}</StepNumber>
                                <StepContent>
                                    <h3>{step.title}</h3>
                                    <p>{step.description}</p>
                                </StepContent>
                            </StepItem>
                        ))}
                    </SymphonySteps>
                </SymphonySection>
                
                <hr style={{ border: 'none', borderTop: '1px solid #eee', maxWidth: '800px', margin: '40px auto' }} />

                {/* --- Why Choose Section --- */}
                <ChooseSection>
                    <ChooseTitle>Why Choose SRK Software for Email Marketing</ChooseTitle>
                    <FeatureGrid>
                        {featureIcons.map((feature, index) => (
                            <FeatureItem key={index}>
                                <FeatureIcon $iconText={feature.iconText} $bgColor={feature.bgColor} />
                                <div>
                                    <h4>{feature.title}</h4>
                                    <p>{feature.description}</p>
                                </div>
                            </FeatureItem>
                        ))}
                    </FeatureGrid>
                </ChooseSection>

                {/* --- Super Services Section --- */}
                <ServicesSection>
                    <ServicesTitle>Our Super Services</ServicesTitle>
                    <p style={{ maxWidth: '600px', margin: '0 auto 40px', color: '#ccc', fontSize: '0.9em' }}>
                        We provide a full spectrum of email marketing solutions designed to take your business from strategy to scale.
                    </p>
                    <ServicesGrid>
                        {services.map((service, index) => (
                            <ServiceCard key={index}>
                                <h3>{service.title}</h3>
                                <p>{service.content}</p>
                            </ServiceCard>
                        ))}
                    </ServicesGrid>
                </ServicesSection>

                {/* --- FAQ Section --- */}
                <FaqSection>
                    <FaqTitle>FAQ</FaqTitle>
                    <FaqContent>
                        {/* --- MAPPING THE POPULATED FAQ DATA --- */}
                        {faqItems.map((faq, index) => (
                            <FaqItem key={index}>
                                <h4>{faq.question}</h4>
                                <p>{faq.answer}</p>
                            </FaqItem>
                        ))}
                    </FaqContent>
                </FaqSection>

            </PageContainer>
        </Home>
    );
};

export default Emailmarketing;


